import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface AuthorityCited { authority_id: string; instrument_type: string; title: string }
export interface AuthorityElement {
  id: string;                       // element_authority row id (review target)
  element_id: string;
  element_name: string;
  verdict: string;                  // authorized | unauthorized | unclear | not_applicable | error
  stage: string | null;            // facial_12b6 | merits_sj | na
  strength: string | null;
  rationale: string | null;
  cited: AuthorityCited[] | null;
  proposer_verdict: string | null;
  judge_verdict: string | null;
  review_status: string | null;     // agreed | split
  human_assessment: string | null;  // allowed | partial | not_allowed | null
  human_note: string | null;
  needs_review: boolean;
  l1_status: string;                 // contradicted | contested | clear (cross-layer gate)
  l1_contradicted: number;           // # supporting paragraphs L1 ruled false
  l1_contested: number;              // # supporting paragraphs disputed at L1
}
export interface AuthorityClaim {
  claim_id: string;
  claim_name: string;
  target: string | null;
  total: number;
  unauthorized: number;
  unclear: number;
  reviewed: number;
  l1_contradicted: number;
  elements: AuthorityElement[];
}

/** Layer 3: per claim -> element -> authority verdict (latest build). */
export function useCaseAuthority(caseId: string | undefined) {
  return useQuery<AuthorityClaim[], Error>({
    queryKey: ["case-authority", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!caseId) return [];
      const { data, error } = await berserkr().rpc("fetch_case_authority", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as AuthorityClaim[]) ?? [];
    },
  });
}

/** Record the human's 3-way authority call on one element: allowed | partial | not_allowed. */
export function useSetAuthorityAssessment(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; assessment: string; note?: string }) => {
      const { error } = await berserkr().rpc("set_element_authority_assessment", {
        p_id: args.id, p_assessment: args.assessment, p_note: args.note ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case-authority", caseId] }),
  });
}
